import {
  auto,
  IHttpService,
  IDirective,
  IQService,
  ICompileProvider,
  IComponentOptions,
  IDirectiveFactory,
  extend,
  IController,
  IControllerService,
  ILocationProvider,
} from "angular";
import { ParamTypeDefinition, TransitionService, UrlMatcherFactory, UrlService } from "@uirouter/core";
import { Ng1StateDeclaration, StateProvider } from "@uirouter/angularjs";
import uiRouter from "@uirouter/angularjs";
import { upgradeModule } from "@uirouter/angular-hybrid";
import { IModalServiceInstance, IModalService, IModalSettings } from "angular-ui-bootstrap";
import lazyLoad from "oclazyload/dist/modules/ocLazyLoad.core.js";
import "oclazyload/dist/modules/ocLazyLoad.loaders.core.js";
import uiBootstrap from "@gtmhub/ui-bootstrap.module";
import { IGtmhubState, IStateInit } from "./models";
import { enableRouteTracing } from "./tracing";
import { urlFromMap } from "./util";
import { ROUTE_TRACING } from "@gtmhub/build";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IAppConfig } from "@gtmhub/env";
import env from "@gtmhub/env/module";
import { configureCdn } from "./cdn";
import { Ng1StateProxy } from "@gtmhub/core/routing";

const $style = "$style";

const replaceTemplateUrl = <T extends { templateUrl?: unknown }>(config: T): T => {
  if (typeof config.templateUrl === "object" && !Array.isArray(config.templateUrl)) {
    const templateUrlMap = config.templateUrl as Record<string, string>;
    config.templateUrl = urlFromMap(templateUrlMap);
  }
  return config;
};

// added upgradeModule for UIrouter to handle upgrading of routes
const mod = angular.module("routing", [uiRouter, upgradeModule.name, uiBootstrap, lazyLoad, env]);
mod.service("Ng1StateProxy", Ng1StateProxy);

// Tell UI-Router that it should wait until all bootstrapping is complete before doing the initial URL synchronization.
mod.config(["$urlServiceProvider", ($urlService: UrlService) => $urlService.deferIntercept()]);

mod.config([
  "$locationProvider",
  "$urlMatcherFactoryProvider",
  ($locationProvider: ILocationProvider, $urlMatcherFactory: UrlMatcherFactory) => {
    $urlMatcherFactory.type("boolean", {
      decode: function (val) {
        return val == "true";
      },
      encode: function (val) {
        return val ? "true" : "false";
      },
      equals: function (this: ParamTypeDefinition, a, b) {
        return this.is(a) && a == b;
      },
      is: function (val) {
        return [true, false].indexOf(val) !== -1;
      },
      pattern: /false|true/,
    });

    $locationProvider.html5Mode(false);
    $locationProvider.hashPrefix("");
  },
]);

mod.config([
  "$stateProvider",
  ($stateProvider: StateProvider) => {
    // this function is created to dynamically append a style in a certain element (custom-styles)
    const appendStyle = (view: IGtmhubState, styleText: string): void => {
      // removing all styles set by other views

      const styleEl = document.createElement("style");
      styleEl.type = "text/css";
      styleEl.textContent = styleText;
      styleEl.id = view["$context"]["name"];
      styleEl.className = "custom-css-style";
      const containerEl = document.head;
      if (!document.getElementById(styleEl.id)) {
        containerEl.appendChild(styleEl);
      }
    };

    type ViewHandler = (view: IGtmhubState) => void;

    // Handle `style` and `styleUrl` configuration
    const styleHandler = (view: IGtmhubState): void => {
      // We check if the Style or StyleURL is part of the current state itself, not inherited from the parent one
      // eslint-disable-next-line no-prototype-builtins
      if (!view["$context"]["data"] || !(view["$context"]["data"].hasOwnProperty("style") || view["$context"]["data"].hasOwnProperty("styleUrl"))) {
        return;
      }

      /* we check if the state has any "resolves" and if not - we define an empty object
       * It has to be done in this way - view["$context"]["resolve"] || {} does not seem to work for some reason */
      if (view["$context"]["resolve"] === undefined) {
        view["$context"]["resolve"] = {};
      }
      Object.assign(view["$context"]["resolve"], {
        [$style]: [
          "$http",
          "$q",
          "appConfig",
          ($http: IHttpService, $q: IQService, appConfig: IAppConfig) => {
            // we handle the case where the style is directly injected
            if (view["$context"]["data"].style) {
              const styleText = typeof view["$context"]["data"].style === "function" ? view["$context"]["data"].style() : view["$context"]["data"].style;
              appendStyle(view, styleText);
              return styleText;
            }
            // we handle the case where the style is injected via one or multiple style urls
            if (view["$context"]["data"].styleUrl) {
              const oneOrManyUrls = typeof view["$context"]["data"].styleUrl === "function" ? view["$context"]["data"].styleUrl() : view["$context"]["data"].styleUrl;
              const urls = Array.isArray(oneOrManyUrls) ? oneOrManyUrls : [oneOrManyUrls];
              return $q
                .all(
                  urls.map((url) => {
                    // modify the condition to allow "http" to be a string combination somewhere after the  first letter of the link
                    // (in case of a random occurrence in base64 or some custom image having a referer
                    // we need to also handle the case of URLs starting with "//domain.xyz"

                    if (appConfig.cdnStorage.enabled && url.indexOf("http") !== 0 && url.indexOf("data:") !== 0 && url.indexOf("//") !== 0) {
                      url = appConfig.cdnStorage.url + url;
                    }
                    return $http.get<string>(url, { headers: { Accept: "text/css" } }).then((response) => response.data);
                  })
                )
                .then((styles) => {
                  const combinedStyles = styles.reduce((all, style) => all + style, "");
                  appendStyle(view, combinedStyles);
                });
            }
          },
        ],
      });
    };

    /* this function creates a resolver for the url parameters so they can be injected within components */
    const paramsHandler = (view: IGtmhubState): void => {
      // we fetch the param names
      const paramNames = Object.keys(view["$context"]["params"]);

      /* we check if the state has any "resolves" and if not - we define an empty object
       * It has to be done in this way - view["$context"]["resolve"] || {} does not seem to work for some reason */
      if (view["$context"]["resolve"] === undefined) {
        view["$context"]["resolve"] = {};
      }

      paramNames.forEach((param) => {
        /* for each param we check if it does not already have a custom set resolver and if there is no resolver we map it to the
         * transition's params*/
        if (!view["$context"]["resolve"][param]) {
          Object.assign(view["$context"]["resolve"], {
            [param]: [
              "$transition$",
              ($transition$) => {
                return $transition$.params()[param];
              },
            ],
          });
        }
        /* for each params we check if it is not optional but also has no default value. If such is the case we assing a default value of null*/
        if (view["$context"]["params"][param].isOptional === false && typeof view["$context"]["params"][param].config.value === "undefined") {
          view["$context"]["params"][param].config.value = null;
          view["$context"]["params"][param].isOptional = true;
        }
      });
    };
    // Replace template URL map in states
    const templateUrlHandler = (view: Ng1StateDeclaration): void => {
      replaceTemplateUrl(view);
    };

    const viewHandlers: ViewHandler[] = [styleHandler, paramsHandler, templateUrlHandler];

    $stateProvider.decorator("views", function (state, parent) {
      const views: { [name: string]: IGtmhubState } = parent(state);

      for (const view of Object.values(views)) {
        for (const handle of viewHandlers) {
          handle(view);
        }
      }
      return views;
    });
  },
]);

mod.config([
  "$provide",
  "$compileProvider",
  ($provide: auto.IProvideService, $compileProvider: ICompileProvider) => {
    // Replace template URL map in components
    const oldComponent = $compileProvider.component;
    $compileProvider.component = function (...args: unknown[]) {
      if (args.length === 1) {
        // called with { [name]: options }
        for (const options of <IComponentOptions[]>Object.values(args[0])) {
          replaceTemplateUrl(options);
        }
      } else if (args.length === 2) {
        // called with componentName, options
        replaceTemplateUrl(args[1] as IComponentOptions);
      } // Replace template URL map in components

      return oldComponent.apply($compileProvider, args);
    };

    // Replace template URL map in directives
    // Directives are registered as factories (i.e. a function),
    // so here we wrap the factory into another factory so that
    // we can access the real directive
    const oldFactory = $provide.factory;
    $provide.factory = function (...args: unknown[]) {
      if (args.length === 2) {
        const name = args[0] as string;
        if (name.endsWith("Directive")) {
          const directiveFactory = args[1] as IDirectiveFactory;
          const directiveFactoryWrapper = function directiveFactoryWithTemplateUrlOverride($injector: auto.IInjectorService) {
            const directives = $injector.invoke<IDirective[]>(directiveFactory, $provide);
            directives.forEach(replaceTemplateUrl);
            return directives;
          };
          directiveFactoryWrapper.$inject = ["$injector"];

          return oldFactory.call($provide, name, directiveFactoryWrapper);
        }
      }
      return oldFactory.apply($provide, args);
    };
  },
]);

mod.run([
  "$uibModal",
  "$transitions",
  ($uibModal: IModalService, $transitions: TransitionService) => {
    // Replace template URL map in $uibModal.open
    const oldOpen = $uibModal.open;
    $uibModal.open = function tracingOpen(options: IModalSettings): IModalServiceInstance {
      replaceTemplateUrl(options);
      return oldOpen.call($uibModal, options);
    };

    // upon successful transition we need to clear all CSS elements that have a different ID compared to our current transition "to" object
    $transitions.onSuccess({}, (trans) => {
      const elements = document.getElementsByClassName("custom-css-style");
      for (let i = 0; i < elements.length; i++) {
        if (trans.to().name.indexOf(elements[i].id) === -1) {
          document.head.removeChild(elements[i]);
        }
      }
    });
  },
]);

mod.config([
  "$provide",
  ($provide: auto.IProvideService) => {
    $provide.decorator("$controller", [
      "$delegate",
      "$rootScope",
      // eslint-disable-next-line @typescript-eslint/ban-types
      ($delegate: Function, $rootScope: ITraceRootScopeService) => {
        const tryRunStateInit = (ctrl: unknown) => {
          const stateInitCtrl = ctrl as IStateInit;
          if (typeof stateInitCtrl.stateInit === "function") {
            $rootScope.traceInit(stateInitCtrl.stateInit.bind(stateInitCtrl));
          }
        };

        const tryPatchOnInit = (ctrl: unknown) => {
          const initCtrl = ctrl as IController;
          if (initCtrl.$onInit) {
            const origOnInit = initCtrl.$onInit;
            initCtrl.$onInit = function () {
              $rootScope.traceInit(() => origOnInit.bind(initCtrl)());
            };
          }
        };
        return function stateInitAwareController(this: IControllerService, ...args: unknown[]) {
          const ctrl = $delegate.apply(this, args);
          if (typeof ctrl === "function") {
            // Angular.js may create a lazy wrapper for initialization, so
            // we will replace it in a way we can call `stateInit`.
            // PS: search for `$controllerInit` in the Angular.js source code
            // if you want to see their implementation.
            if ("instance" in ctrl && "identifier" in ctrl) {
              return extend(
                function stateAwareControllerInit() {
                  const instance = ctrl();
                  tryRunStateInit(instance);
                  tryPatchOnInit(instance);
                  return instance;
                },
                {
                  instance: ctrl.instance,
                  identifier: ctrl.identifier,
                }
              );
            }
          } else {
            tryRunStateInit(ctrl);
          }
          return ctrl;
        };
      },
    ]);
  },
]);

if (ROUTE_TRACING) {
  enableRouteTracing(mod);
}

configureCdn(mod);

export default mod.name;
